import { signIn, signUp, resetPassword, lookupGroupByCode } from '../auth.js';
import { showToast } from '../utils/toast.js';

let activeTab = 'signin';
let groupMode = 'join'; // 'join', 'create', or 'solo'

export async function openAuthModal(defaultTab = 'signin', initialGroupCode = '') {
  activeTab = defaultTab;
  const root = document.getElementById('auth-modal-root');
  if (!root) return;

  let prefilledGroup = null;
  if (initialGroupCode) {
    try {
      prefilledGroup = await lookupGroupByCode(initialGroupCode);
    } catch (e) {
      console.warn(e);
    }
  }

  root.innerHTML = `
    <div class="modal-backdrop" id="auth-modal-backdrop">
      <div class="modal-card" id="auth-modal-card" style="max-width: 520px;">
        <div class="modal-header">
          <div style="display:flex; align-items:center; gap:0.5rem;">
            <div class="brand-icon-box" style="width:32px; height:32px; font-size:1rem;">
              <i class="fa-solid fa-cross"></i>
            </div>
            <h3 class="modal-title" id="auth-modal-title">
              ${activeTab === 'signin' ? 'Welcome Back' : activeTab === 'signup' ? 'Begin Your Journey' : 'Reset Password'}
            </h3>
          </div>
          <button class="modal-close-btn" id="close-auth-modal" aria-label="Close modal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div style="display:flex; border-bottom:1px solid var(--border-subtle); background:rgba(0,0,0,0.1);">
          <button class="nav-link-btn ${activeTab === 'signin' ? 'active' : ''}" id="tab-signin-btn" style="flex:1; justify-content:center; border-radius:0; padding:0.75rem;">
            Sign In
          </button>
          <button class="nav-link-btn ${activeTab === 'signup' ? 'active' : ''}" id="tab-signup-btn" style="flex:1; justify-content:center; border-radius:0; padding:0.75rem;">
            Register
          </button>
        </div>

        <div class="modal-body">
          <div id="auth-error-banner" style="display:none; padding:0.75rem; border-radius:var(--radius-md); background:rgba(244,63,94,0.1); border:1px solid var(--accent-rose); color:var(--accent-rose); font-size:0.85rem; margin-bottom:1rem;"></div>

          ${activeTab === 'signin' ? `
            <form id="signin-form">
              <div class="form-group">
                <label class="form-label" for="signin-email">Email Address</label>
                <input type="email" id="signin-email" class="form-input" placeholder="you@domain.com" required autocomplete="email" />
              </div>
              <div class="form-group">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.4rem;">
                  <label class="form-label" for="signin-password" style="margin:0;">Password</label>
                  <a href="#" id="link-forgot-pw" style="font-size:0.8rem; color:var(--accent-blue);">Forgot password?</a>
                </div>
                <input type="password" id="signin-password" class="form-input" placeholder="••••••••" required autocomplete="current-password" />
              </div>
              <button type="submit" id="submit-signin-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem; margin-top:0.5rem;">
                <i class="fa-solid fa-right-to-bracket"></i> Sign In to Course
              </button>
            </form>
          ` : activeTab === 'signup' ? `
            <form id="signup-form">
              <!-- Group Enrollment Selector -->
              <div style="margin-bottom:1.25rem; background:rgba(255,255,255,0.03); border:1px solid var(--border-subtle); border-radius:var(--radius-md); padding:0.75rem;">
                <label class="form-label" style="margin-bottom:0.4rem; font-size:0.8rem; text-transform:uppercase; letter-spacing:0.04em; color:var(--accent-blue);">
                  <i class="fa-solid fa-people-group"></i> Group Registration Option
                </label>
                <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:0.4rem; margin-bottom:0.75rem;">
                  <button type="button" class="btn-secondary group-mode-btn ${groupMode === 'join' ? 'active-mode' : ''}" data-mode="join" style="padding:0.4rem 0.5rem; font-size:0.78rem; justify-content:center;">
                    Join Group
                  </button>
                  <button type="button" class="btn-secondary group-mode-btn ${groupMode === 'create' ? 'active-mode' : ''}" data-mode="create" style="padding:0.4rem 0.5rem; font-size:0.78rem; justify-content:center;">
                    Start Group
                  </button>
                  <button type="button" class="btn-secondary group-mode-btn ${groupMode === 'solo' ? 'active-mode' : ''}" data-mode="solo" style="padding:0.4rem 0.5rem; font-size:0.78rem; justify-content:center;">
                    Individual
                  </button>
                </div>

                <div id="group-field-join" style="display:${groupMode === 'join' ? 'block' : 'none'};">
                  <div style="display:flex; gap:0.5rem;">
                    <input type="text" id="signup-group-code" class="form-input" placeholder="Enter Invite Code (e.g. WALK-A4B2)" value="${initialGroupCode || ''}" style="text-transform:uppercase; font-weight:600; letter-spacing:0.05em;" />
                    <button type="button" id="verify-code-btn" class="btn-secondary" style="padding:0 0.8rem; font-size:0.8rem; white-space:nowrap;">
                      Verify
                    </button>
                  </div>
                  <div id="code-verified-msg" style="font-size:0.8rem; margin-top:0.4rem; ${prefilledGroup ? 'color:var(--accent-emerald); display:block;' : 'display:none;'}">
                    <i class="fa-solid fa-circle-check"></i> Group: <strong>${prefilledGroup?.name || ''}</strong>
                  </div>
                </div>

                <div id="group-field-create" style="display:${groupMode === 'create' ? 'block' : 'none'};">
                  <input type="text" id="signup-new-group-name" class="form-input" placeholder="New Group Name (e.g. Grace Fellowship Men)" />
                  <div style="font-size:0.75rem; color:var(--text-secondary); margin-top:0.35rem;">
                    You will become the leader and receive a shareable invite code upon sign up.
                  </div>
                </div>

                <div id="group-field-solo" style="display:${groupMode === 'solo' ? 'block' : 'none'};">
                  <input type="text" id="signup-cohort-text" class="form-input" placeholder="Church / Community (Optional)" value="Independent Student" />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label" for="signup-name">Full Name</label>
                <input type="text" id="signup-name" class="form-input" placeholder="e.g. John Doe" required autocomplete="name" />
              </div>
              <div class="form-group">
                <label class="form-label" for="signup-display">Display Name (Visible in Group Discussions)</label>
                <input type="text" id="signup-display" class="form-input" placeholder="e.g. John D." required />
              </div>
              <div class="form-group">
                <label class="form-label" for="signup-email">Email Address</label>
                <input type="email" id="signup-email" class="form-input" placeholder="you@domain.com" required autocomplete="email" />
              </div>
              <div class="form-group">
                <label class="form-label" for="signup-password">Password (min 6 characters)</label>
                <input type="password" id="signup-password" class="form-input" placeholder="••••••••" minlength="6" required autocomplete="new-password" />
              </div>
              <button type="submit" id="submit-signup-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem; margin-top:0.5rem;">
                <i class="fa-solid fa-user-plus"></i> Complete Registration
              </button>
            </form>
          ` : `
            <form id="reset-form">
              <p style="color:var(--text-secondary); font-size:0.9rem; margin-bottom:1.25rem;">
                Enter your email address and we will send you a link to reset your password.
              </p>
              <div class="form-group">
                <label class="form-label" for="reset-email">Email Address</label>
                <input type="email" id="reset-email" class="form-input" placeholder="you@domain.com" required />
              </div>
              <button type="submit" id="submit-reset-btn" class="btn-primary" style="width:100%; justify-content:center; padding:0.8rem;">
                Send Reset Link
              </button>
              <div style="text-align:center; margin-top:1rem;">
                <a href="#" id="link-back-signin" style="font-size:0.85rem;">Back to Sign In</a>
              </div>
            </form>
          `}
        </div>
      </div>
    </div>
  `;

  // Attach handlers
  const backdrop = document.getElementById('auth-modal-backdrop');
  const closeBtn = document.getElementById('close-auth-modal');
  const errorBanner = document.getElementById('auth-error-banner');

  const close = () => { root.innerHTML = ''; };
  backdrop.onclick = (e) => { if (e.target === backdrop) close(); };
  closeBtn.onclick = close;

  const tabIn = document.getElementById('tab-signin-btn');
  const tabUp = document.getElementById('tab-signup-btn');
  if (tabIn) tabIn.onclick = () => openAuthModal('signin');
  if (tabUp) tabUp.onclick = () => openAuthModal('signup');

  // Group mode switcher buttons
  const modeBtns = root.querySelectorAll('.group-mode-btn');
  const paneJoin = document.getElementById('group-field-join');
  const paneCreate = document.getElementById('group-field-create');
  const paneSolo = document.getElementById('group-field-solo');

  modeBtns.forEach(btn => {
    btn.onclick = () => {
      groupMode = btn.getAttribute('data-mode');
      modeBtns.forEach(b => b.classList.remove('active-mode'));
      btn.classList.add('active-mode');

      if (paneJoin) paneJoin.style.display = groupMode === 'join' ? 'block' : 'none';
      if (paneCreate) paneCreate.style.display = groupMode === 'create' ? 'block' : 'none';
      if (paneSolo) paneSolo.style.display = groupMode === 'solo' ? 'block' : 'none';
    };
  });

  // Verify group code button
  const verifyBtn = document.getElementById('verify-code-btn');
  const codeInput = document.getElementById('signup-group-code');
  const verifiedMsg = document.getElementById('code-verified-msg');

  if (verifyBtn && codeInput) {
    verifyBtn.onclick = async () => {
      const code = codeInput.value.trim();
      if (!code) {
        showToast('Please enter a group invite code', 'warning');
        return;
      }
      verifyBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>';
      const found = await lookupGroupByCode(code);
      verifyBtn.innerHTML = 'Verify';

      if (found) {
        verifiedMsg.style.display = 'block';
        verifiedMsg.style.color = 'var(--accent-emerald)';
        verifiedMsg.innerHTML = `<i class="fa-solid fa-circle-check"></i> Group: <strong>${found.name}</strong>`;
      } else {
        verifiedMsg.style.display = 'block';
        verifiedMsg.style.color = 'var(--accent-rose)';
        verifiedMsg.innerHTML = `<i class="fa-solid fa-circle-xmark"></i> Group code not found.`;
      }
    };
  }

  const forgotLink = document.getElementById('link-forgot-pw');
  if (forgotLink) forgotLink.onclick = (e) => { e.preventDefault(); openAuthModal('reset'); };

  const backLink = document.getElementById('link-back-signin');
  if (backLink) backLink.onclick = (e) => { e.preventDefault(); openAuthModal('signin'); };

  // Form submits
  const signinForm = document.getElementById('signin-form');
  if (signinForm) {
    signinForm.onsubmit = async (e) => {
      e.preventDefault();
      const email = document.getElementById('signin-email').value.trim();
      const password = document.getElementById('signin-password').value;
      const submitBtn = document.getElementById('submit-signin-btn');

      try {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Signing In...';
        await signIn({ email, password });
        showToast('Successfully signed in!', 'success');
        close();
      } catch (err) {
        errorBanner.style.display = 'block';
        errorBanner.textContent = err.message || 'Failed to sign in. Please verify your credentials.';
      } finally {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="fa-solid fa-right-to-bracket"></i> Sign In to Course';
      }
    };
  }

  const signupForm = document.getElementById('signup-form');
  if (signupForm) {
    signupForm.onsubmit = async (e) => {
      e.preventDefault();
      const fullName = document.getElementById('signup-name').value.trim();
      const displayName = document.getElementById('signup-display').value.trim();
      const email = document.getElementById('signup-email').value.trim();
      const password = document.getElementById('signup-password').value;
      const submitBtn = document.getElementById('submit-signup-btn');

      let groupCode = '';
      let newGroupName = '';
      let cohortName = 'Independent Student';

      if (groupMode === 'join') {
        groupCode = document.getElementById('signup-group-code')?.value.trim() || '';
        cohortName = 'Group Member';
      } else if (groupMode === 'create') {
        newGroupName = document.getElementById('signup-new-group-name')?.value.trim() || '';
        if (!newGroupName) {
          errorBanner.style.display = 'block';
          errorBanner.textContent = 'Please enter a name for your new group.';
          return;
        }
        cohortName = newGroupName;
      } else {
        cohortName = document.getElementById('signup-cohort-text')?.value.trim() || 'Independent Student';
      }

      try {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Creating Account...';
        await signUp({ fullName, displayName, cohortName, groupCode, newGroupName, email, password });
        showToast('Account created successfully! Welcome to Discipleship 101.', 'success');
        close();
      } catch (err) {
        errorBanner.style.display = 'block';
        errorBanner.textContent = err.message || 'Failed to create account.';
      } finally {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="fa-solid fa-user-plus"></i> Complete Registration';
      }
    };
  }

  const resetForm = document.getElementById('reset-form');
  if (resetForm) {
    resetForm.onsubmit = async (e) => {
      e.preventDefault();
      const email = document.getElementById('reset-email').value.trim();
      const submitBtn = document.getElementById('submit-reset-btn');

      try {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending...';
        await resetPassword(email);
        showToast('Password reset email sent! Check your inbox.', 'success');
        close();
      } catch (err) {
        errorBanner.style.display = 'block';
        errorBanner.textContent = err.message || 'Failed to send reset link.';
      } finally {
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'Send Reset Link';
      }
    };
  }
}
